import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../ChatAvatarIcons';
import './StimulusAnnotationText.css';

const TOOLTIP_GAP = 9;
const VIEWPORT_MARGIN = 12;
const TOOLTIP_HOVER_HANDOFF_MS = 120;

function clamp(value, minimum, maximum) {
  if (maximum < minimum) return minimum;
  return Math.min(Math.max(value, minimum), maximum);
}

function calculateTooltipPosition(anchorRect, tooltipSize, viewport) {
  const viewportRight = viewport.left + viewport.width;
  const viewportBottom = viewport.top + viewport.height;
  const width = Math.min(tooltipSize.width, Math.max(0, viewport.width - (VIEWPORT_MARGIN * 2)));
  const height = Math.min(tooltipSize.height, Math.max(0, viewport.height - (VIEWPORT_MARGIN * 2)));
  const left = clamp(
    anchorRect.left + (anchorRect.width / 2) - (width / 2),
    viewport.left + VIEWPORT_MARGIN,
    viewportRight - VIEWPORT_MARGIN - width,
  );
  const belowTop = anchorRect.bottom + TOOLTIP_GAP;
  const aboveTop = anchorRect.top - TOOLTIP_GAP - height;
  const roomBelow = viewportBottom - anchorRect.bottom;
  const roomAbove = anchorRect.top - viewport.top;
  const placeAbove = belowTop + height > viewportBottom - VIEWPORT_MARGIN && roomAbove > roomBelow;
  const desiredTop = placeAbove ? aboveTop : belowTop;
  const top = clamp(
    desiredTop,
    viewport.top + VIEWPORT_MARGIN,
    viewportBottom - VIEWPORT_MARGIN - height,
  );

  return {
    left,
    top,
    placement: placeAbove ? 'above' : 'below',
  };
}

function buildSegments(text, annotations) {
  const validAnnotations = (annotations || [])
    .filter((annotation) => (
      Number.isInteger(annotation?.start) &&
      Number.isInteger(annotation?.end) &&
      annotation.start >= 0 &&
      annotation.end > annotation.start &&
      annotation.end <= text.length &&
      text.slice(annotation.start, annotation.end) === annotation.quote &&
      annotation.explanation
    ))
    .sort((left, right) => left.start - right.start);

  const segments = [];
  let cursor = 0;
  validAnnotations.forEach((annotation, index) => {
    if (annotation.start < cursor) {
      return;
    }
    if (annotation.start > cursor) {
      segments.push({ type: 'text', text: text.slice(cursor, annotation.start) });
    }
    segments.push({ type: 'annotation', annotation, index });
    cursor = annotation.end;
  });
  if (cursor < text.length) {
    segments.push({ type: 'text', text: text.slice(cursor) });
  }
  return segments;
}

function StimulusTeachingTooltip({
  anchor,
  annotation,
  theme,
  tooltipId,
  t,
  onMouseEnter,
  onMouseLeave,
}) {
  const [position, setPosition] = useState(null);
  const tooltipRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchor) {
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewport = {
        left: visualViewport?.offsetLeft || 0,
        top: visualViewport?.offsetTop || 0,
        width: visualViewport?.width || window.innerWidth,
        height: visualViewport?.height || window.innerHeight,
      };
      const rootFontSize = Number.parseFloat(
        window.getComputedStyle(document.documentElement).fontSize,
      ) || 16;
      const tooltipSize = {
        width: tooltipRef.current?.offsetWidth || Math.min(20 * rootFontSize, viewport.width - 24),
        height: tooltipRef.current?.offsetHeight || 190,
      };
      setPosition(calculateTooltipPosition(rect, tooltipSize, viewport));
    };

    updatePosition();
    const animationFrame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [anchor]);

  if (!position) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      id={tooltipId}
      className={`stimulus-teaching-tooltip stimulus-teaching-tooltip--${position.placement} stimulus-teaching-tooltip--${theme === 'dark' ? 'dark' : 'light'}`}
      style={{ left: position.left, top: position.top }}
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="stimulus-teaching-tooltip__message">
        <span className="stimulus-teaching-tooltip__avatar" aria-hidden="true">
          <AimieRobotAvatarIcon />
        </span>
        <p>{annotation.explanation}</p>
      </div>
      {annotation.suggested_response && (
        <div className="stimulus-teaching-tooltip__example">
          <span>{t('specific_skill_annotation_example')}</span>
          <q>{annotation.suggested_response}</q>
        </div>
      )}
      <div className="stimulus-teaching-tooltip__settings-hint">
        <span>Hide or restore these: <strong>☰ → Settings → Skills Annotations</strong></span>
      </div>
    </div>,
    document.body,
  );
}

function StimulusAnnotationText({ text = '', annotations = [], theme = 'dark', t, suppressed = false }) {
  const [active, setActive] = useState(null);
  const closeTimeoutRef = useRef(null);
  const segments = useMemo(() => buildSegments(text, annotations), [annotations, text]);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setActive(null);
    }, TOOLTIP_HOVER_HANDOFF_MS);
  }, [cancelScheduledClose]);

  useEffect(() => () => cancelScheduledClose(), [cancelScheduledClose]);

  useEffect(() => {
    if (suppressed) {
      cancelScheduledClose();
      setActive(null);
    }
  }, [cancelScheduledClose, suppressed]);

  return (
    <div className="message-text-plain stimulus-annotation-text">
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'text') {
          return <React.Fragment key={`text-${segmentIndex}`}>{segment.text}</React.Fragment>;
        }

        const tooltipId = `stimulus-teaching-tooltip-${segment.annotation.turn_id}-${segment.index}`;
        const isActive = active?.segmentIndex === segmentIndex;
        return (
          <React.Fragment key={`annotation-${segment.annotation.turn_id}-${segment.annotation.start}`}>
            <mark
              className={`stimulus-annotation-highlight${isActive ? ' is-active' : ''}`}
              tabIndex={0}
              aria-describedby={isActive ? tooltipId : undefined}
              onMouseEnter={(event) => {
                if (!suppressed) {
                  cancelScheduledClose();
                  setActive({ segmentIndex, anchor: event.currentTarget });
                }
              }}
              onMouseLeave={scheduleClose}
              onFocus={(event) => {
                if (!suppressed) setActive({ segmentIndex, anchor: event.currentTarget });
              }}
              onBlur={() => setActive(null)}
              onClick={(event) => {
                if (!suppressed) setActive({ segmentIndex, anchor: event.currentTarget });
              }}
            >
              {segment.annotation.quote}
            </mark>
            {isActive && (
              <StimulusTeachingTooltip
                anchor={active.anchor}
                annotation={segment.annotation}
                theme={theme}
                tooltipId={tooltipId}
                t={t}
                onMouseEnter={cancelScheduledClose}
                onMouseLeave={scheduleClose}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export { buildSegments, calculateTooltipPosition };
export default StimulusAnnotationText;
